import * as am4charts from '@amcharts/amcharts4/charts';
import * as am4core from '@amcharts/amcharts4/core';
import { DateTime } from 'luxon';
// CORE
import * as React from 'react';
import { useCallback, useContext, useLayoutEffect, useMemo } from 'react';
import { useUpdate } from 'react-use';

import Box from '@mui/material/Box';
import Divider from '@mui/material/Divider';
import FormControl from '@mui/material/FormControl';
import FormControlLabel from '@mui/material/FormControlLabel';
import Grow from '@mui/material/Grow';
import MenuItem from '@mui/material/MenuItem';
import Paper from '@mui/material/Paper';
import Radio from '@mui/material/Radio';
import RadioGroup from '@mui/material/RadioGroup';
import TextField from '@mui/material/TextField';
// MUI
import Typography from '@mui/material/Typography';
import { useTheme } from '@mui/material/styles';

import { makeStyles } from 'tss-react/mui';

// DATA

// SHARED/LOCAL
import QButton from 'components/QButton';
import AmountField from 'components/QuickenControls/AmountField';
import useXYChart from 'hooks/useXYChart';
import { AddGoalContext } from './state';

const useStyles = makeStyles()((theme) => ({
  chart: {
    width: 330,
    height: 380,
  },
  chartHeader: {
    borderRadius: `${theme.shape.borderRadius * 3}px ${theme.shape.borderRadius * 3}px 0 0`,
    background: theme.palette.background.layer1,
    height: 65,
  },
  divider: {
    width: 1,
    margin: `0 ${theme.spacing(1)}`,
    background: theme.palette.divider,
    height: '60%',
  },
  startDateDivider: {
    margin: `${theme.spacing(2)} 0`,
  },
  radioGroup: {
    flexDirection: 'row',
  },
  dateDropdown: {
    minWidth: 100,
  },
  monthSelect: {
    marginRight: theme.spacing(1),
    minWidth: 100,
  },
  suggestionBox: {
    borderRadius: theme.shape.borderRadius * 3,
    padding: `${theme.spacing(1)} ${theme.spacing(2)}`,
    background: theme.palette.info.light,
    marginTop: theme.spacing(2),
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
  },
  suggestionAdjustment: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    background: theme.palette.background.paper,
    borderRadius: 'inherit',
    padding: `${theme.spacing(1)} ${theme.spacing(2)}`,
    width: '100%',
    margin: `${theme.spacing(0.5)} 0`,
  },
  errorText: {
    color: theme.palette.error.main,
  },
  soFar: {
    whiteSpace: 'nowrap',
  },
}));

const MONTHS_SHORT = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

// @ts-expect-error FIXME TS7006
function GoalTargets(_props) {
  const { dispatch, state } = useContext(AddGoalContext);
  const { targetAmount, savedSoFar, targetDate, startDate, contributionAmount } = state;
  const { classes } = useStyles();
  const theme = useTheme();
  const causeReRender = useUpdate();
  const { chart, buildTooltipOn, buildTooltipSeriesInfo, yAxis } = useXYChart('target-goal-chart');

  // Callbacks
  const handleSetTargetDateChange = useCallback(
    // @ts-expect-error FIXME TS7006
    (e) => {
      dispatch({
        type: 'update',
        payload: {
          targetDate: e.target.value === 'set' ? DateTime.local().plus({ years: 1 }).endOf('month') : null,
        },
      });
    },
    [dispatch],
  );

  const handleDateChange = useCallback(
    // @ts-expect-error FIXME TS7031
    ({ target: { value, name } }) => {
      switch (name) {
        case 'targetDateMonth':
          dispatch({
            type: 'update',
            payload: { targetDate: targetDate.set({ month: MONTHS_SHORT.indexOf(value) + 1 }) },
          });
          break;
        case 'targetDateYear':
          dispatch({ type: 'update', payload: { targetDate: targetDate.set({ year: value }) } });
          break;
        case 'startDateMonth':
          dispatch({
            type: 'update',
            payload: { startDate: startDate.set({ month: MONTHS_SHORT.indexOf(value) + 1 }) },
          });
          break;
        case 'startDateYear':
          dispatch({ type: 'update', payload: { startDate: startDate.set({ year: value }) } });
          break;
        default:
          break;
      }
    },
    [targetDate, startDate, dispatch],
  );

  const handleContributionAmountChange = useCallback(
    // @ts-expect-error FIXME TS7006
    (e) => {
      dispatch({ type: 'update', payload: { contributionAmount: Number(e.target.value) || 0 } });
    },
    [dispatch],
  );

  // Memoized values
  const errors = useMemo(() => {
    const retErr: any = {};

    if (targetDate && targetDate <= startDate) {
      retErr.targetDate = 'your target date must come after the start date';
      retErr.startDate = 'your start date must come before the target date';
    }
    if (contributionAmount > targetAmount) {
      retErr.contributionAmount = 'your contribution amount must be less than the target amount';
    }

    return retErr;
  }, [startDate, targetAmount, targetDate, contributionAmount]);

  const adjustmentSuggestions = useMemo(() => {
    if (contributionAmount === 0) return null;

    const projectedFinishDate = startDate
      .endOf('month')
      .plus({ months: Math.ceil((targetAmount - savedSoFar - contributionAmount) / contributionAmount) });
    const projectedFinishDateIsOutOfBounds = projectedFinishDate.year > DateTime.local().year + 50; // / only allow 50 years into future

    if (!targetDate) {
      return (
        <Typography variant='paragraphRegular'>
          <span role='img' aria-label='hang-ten-emoji'>
            🤙
          </span>
          &nbsp;You&apos;ll hit your goal by&nbsp;{projectedFinishDate.toFormat('MMMM yyyy')}
        </Typography>
      );
    }

    let finishDiffInMonths = projectedFinishDate.diff(targetDate, 'months').months;
    // sometimes the diff comes out to a very small number but really means no difference
    finishDiffInMonths = finishDiffInMonths >= -0.001 && finishDiffInMonths <= 0.001 ? 0 : finishDiffInMonths;
    if (finishDiffInMonths === 0) {
      return (
        <Typography variant='paragraphRegular'>
          <span role='img' aria-label='hang-ten-emoji'>
            🤙
          </span>
          &nbsp;You&apos;re all set to achieve this goal on time
        </Typography>
      );
    }
    // if here then the user is either going to be late or early
    const possibleContributionAmountChange =
      Math.ceil(((targetAmount - savedSoFar) / Math.ceil(targetDate.diff(startDate, 'months').months)) * 100) / 100;
    return (
      <>
        <Typography variant='paragraphRegular'>
          You&apos;ll finish this goal&nbsp;{finishDiffInMonths > 0 ? 'late' : 'early'}
        </Typography>
        {!projectedFinishDateIsOutOfBounds && (
          <div className={classes.suggestionAdjustment}>
            <Typography variant='paragraphRegular' id='create_goal_change_date_text'>
              Change date to&nbsp;{projectedFinishDate.toFormat('MMMM yyyy')}
            </Typography>
            <QButton
              onClick={() => dispatch({ type: 'update', payload: { targetDate: projectedFinishDate } })}
              variant='text'
              id='create_goal_date_change_suggestion'
            >
              CHANGE
            </QButton>
          </div>
        )}
        <div className={classes.suggestionAdjustment}>
          <Typography variant='paragraphRegular'>
            Change your monthly contribution to&nbsp;${possibleContributionAmountChange.toFixed(2)}
          </Typography>
          <QButton
            onClick={() =>
              dispatch({ type: 'update', payload: { contributionAmount: possibleContributionAmountChange } })
            }
            variant='text'
            id='create_goal_contribution_change_suggestion'
          >
            CHANGE
          </QButton>
        </div>
      </>
    );
  }, [startDate, targetDate, contributionAmount, targetAmount, classes.suggestionAdjustment, dispatch, savedSoFar]);

  // This needs to come after the adjustmentSuggestions memoized value
  const handleContinue = useCallback(() => {
    dispatch({ type: 'update', payload: { tabIndex: 3, ignoredSuggestions: Boolean(adjustmentSuggestions) } });
  }, [dispatch, adjustmentSuggestions]);

  // Effects
  useLayoutEffect(() => {
    if (!chart) return;

    if (contributionAmount <= 0) {
      // handle edge cases for if the user has already saved he amount for the goal
      // @ts-expect-error FIXME TS2339
      chart.data = [{ category: startDate.toFormat('MMM yyyy'), target: targetAmount }];
      return;
    }

    // NOTE: it's important to remember that startDate is always the start of the month
    //    and targetDate/endDate is always at the end of the month
    const endDate = targetDate || startDate.plus({ months: Math.floor(targetAmount / contributionAmount) });
    const data = [
      {
        category: startDate.toFormat('MMM yyyy'),
        target: Math.max(savedSoFar, contributionAmount),
      },
    ];
    if (endDate.diff(startDate, 'months').months > 2) {
      // only add the center date if more than two months diff
      const midDate = startDate
        .plus({ months: Math.floor(endDate.diff(startDate, 'months').months / 2) })
        .endOf('month');
      data.push({
        category: midDate.toFormat('MMM yyyy'),
        target: Math.abs((savedSoFar || 0) + contributionAmount * midDate.diff(startDate, 'months').months),
      });
    }
    if (endDate.diff(startDate, 'months').months > 1) {
      // only add final date if more than 1 month
      data.push({
        category: endDate.toFormat('MMM yyyy'),
        target: Math.max(targetAmount, Math.abs(contributionAmount * endDate.diff(startDate, 'months').months)),
      });
    }
    // @ts-expect-error FIXME TS2339
    chart.data = data;
  }, [targetAmount, savedSoFar, chart, startDate, contributionAmount, targetDate]);

  useLayoutEffect(() => {
    if (!chart) return;

    // @ts-expect-error FIXME TS2339
    chart.series?.clear();
    // @ts-expect-error FIXME TS2339
    const series = chart.series.push(new am4charts.LineSeries());
    series.name = 'target';
    series.fill = am4core.color(theme.palette.success.main); // todo raw color use
    series.stroke = am4core.color(theme.palette.success.main); // todo raw color use
    series.strokeDasharray = '6,2';
    series.strokeWidth = 2;
    series.dataFields.categoryX = 'category';
    series.dataFields.valueY = 'target';

    // @ts-expect-error FIXME TS18047
    yAxis.min = 0;
    // @ts-expect-error FIXME TS2339
    chart.maskBullets = false;

    buildTooltipOn(series);
    series.tooltipHTML = `<div style="padding: 8px;" >${buildTooltipSeriesInfo(series)}</div>`;

    const bullet = series.bullets.push(new am4charts.CircleBullet());
    bullet.circle.radius = 4;
    bullet.circle.fill = am4core.color(theme.palette.background.paper); // todo raw color use
    bullet.circle.strokeWidth = 2;
    bullet.circle.stroke = am4core.color(theme.palette.savingsGoal.spentBar); // todo raw color use

    const hoverState = bullet.states.create('hover');
    hoverState.properties.scale = 1.5;
  }, [chart, buildTooltipSeriesInfo, buildTooltipOn, yAxis, theme.palette]);

  let leftToSave = Number(targetAmount) - Number(savedSoFar);
  if (leftToSave < 0) {
    leftToSave = 0;
  }

  return (
    <Box display='flex' padding='16px 24px 24px'>
      <Box minWidth={360} display='flex' flex={1} paddingRight={2} flexDirection='column'>
        <Typography variant='bodyRegular'>Target goal date</Typography>
        <FormControl component='fieldset'>
          <RadioGroup
            className={classes.radioGroup}
            name='set-target-date'
            value={targetDate ? 'set' : 'dont'}
            onChange={handleSetTargetDateChange}
          >
            <FormControlLabel value='set' control={<Radio id='create_goal_set_target_date' />} label='Set' />
            <FormControlLabel
              value='dont'
              control={<Radio id='create_goal_dont_set_target_date' />}
              label="Don't set"
            />
          </RadioGroup>
        </FormControl>
        {/* =================== Target Date =================== */}
        <Box id='create_goal_target_date_container' marginTop={2} display='flex' alignItems='center'>
          <TextField
            variant='filled'
            label='Month'
            name='targetDateMonth'
            value={targetDate?.monthShort || ''}
            disabled={!targetDate}
            select
            onChange={handleDateChange}
            id='target_date_month_dropdown'
            className={classes.monthSelect}
            error={Boolean(errors.targetDate)}
          >
            {MONTHS_SHORT.map((month) => (
              <MenuItem key={`target-month-${month}`} value={month}>
                {month}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            variant='filled'
            label='Year'
            name='targetDateYear'
            value={targetDate?.year || ''}
            disabled={!targetDate}
            select
            onChange={handleDateChange}
            className={classes.dateDropdown}
            id='target_date_year_dropdown'
            error={Boolean(errors.targetDate)}
          >
            {Array(20)
              .fill(0)
              .map(
                (
                  _val,
                  index, // 20 because we want 19 years in future and this starts on this year
                ) => (
                  <MenuItem key={`target-year-${DateTime.local().year + index}`} value={DateTime.local().year + index}>
                    {DateTime.local().year + index}
                  </MenuItem>
                ),
              )}
            {targetDate?.year > DateTime.local().year + 19 && ( // if the date is out of the bounds of the selection we need a custom item for it to show
              <MenuItem key={`target-year-${targetDate.year}`} value={targetDate.year}>
                {targetDate.year}
              </MenuItem>
            )}
          </TextField>
        </Box>
        <Typography variant='captionRegular' className={classes.errorText}>
          {errors.targetDate}
        </Typography>

        {/* =================== Monthly Contribution =================== */}
        <Box marginTop={2}>
          <AmountField
            label='Monthly contribution'
            showAmountAdornment
            currencySymbol='USD'
            amountType='amount'
            textFieldVariant='filled'
            editable
            omitCents={false}
            onChange={handleContributionAmountChange}
            value={contributionAmount || 0}
            error={Boolean(errors.contributionAmount)}
            helperText={errors.contributionAmount || ' '}
            id='create_goal_monthly_contribution_field'
            name='contributionAmount'
            onBlur={causeReRender}
          />
        </Box>

        {/* =================== Start Date =================== */}
        {savedSoFar === 0 && (
          <>
            <Divider className={classes.startDateDivider} />
            <Typography variant='bodyRegular'>Start date</Typography>
            <Box id='create_goal_start_date_container' marginTop={2} display='flex' alignItems='center'>
              <TextField
                variant='filled'
                label='Month'
                name='startDateMonth'
                value={startDate?.monthShort}
                select
                onChange={handleDateChange}
                id='start_date_month_dropdown'
                className={classes.monthSelect}
                error={Boolean(errors.startDate)}
              >
                {MONTHS_SHORT.map((month) => (
                  <MenuItem key={`start-month-${month}`} value={month}>
                    {month}
                  </MenuItem>
                ))}
              </TextField>
              <TextField
                variant='filled'
                label='Year'
                name='startDateYear'
                value={startDate?.year}
                select
                onChange={handleDateChange}
                id='start_date_year_dropdown'
                className={classes.dateDropdown}
                error={Boolean(errors.startDate)}
              >
                {Array(5)
                  .fill(0)
                  .map((_val, index) => (
                    <MenuItem key={`start-year-${DateTime.local().year + index}`} value={DateTime.local().year + index}>
                      {DateTime.local().year + index}
                    </MenuItem>
                  ))}
              </TextField>
            </Box>
            <Typography variant='captionRegular' className={classes.errorText}>
              {errors.startDate}
            </Typography>
          </>
        )}
        <Grow in={contributionAmount !== 0 && Object.keys(errors).length === 0}>
          <Box className={classes.suggestionBox}>{adjustmentSuggestions}</Box>
        </Grow>
      </Box>
      <Box display='flex' flexDirection='column' justifyContent='space-between' flex={1}>
        <Paper elevation={2}>
          <Box className={classes.chartHeader} display='flex' overflow='hidden' alignItems='center'>
            <Box padding={1} flex={1}>
              <Typography variant='paragraphRegular'>Goal amount</Typography>
              <AmountField omitCents editable={false} value={targetAmount} />
            </Box>
            <Box padding={1} flex={1}>
              <Typography variant='paragraphRegular' className={classes.soFar}>
                Saved so far
              </Typography>
              <AmountField omitCents editable={false} value={savedSoFar} />
            </Box>
            <div className={classes.divider} />
            <Box padding={1} flex={1}>
              <Typography variant='paragraphRegular'>Left to save</Typography>
              <AmountField omitCents editable={false} value={leftToSave} />
            </Box>
          </Box>
          <div className={classes.chart} id='target-goal-chart' />
        </Paper>
        <Box marginTop={3} display='flex' justifyContent='flex-end' width='100%'>
          <QButton
            variant='contained'
            onClick={handleContinue}
            disabled={Object.keys(errors).length > 0}
            id='create_goal_continue_button'
          >
            continue
          </QButton>
        </Box>
      </Box>
    </Box>
  );
}

export default GoalTargets;
